import {
  enterviceDelete, enterviceGet, entervicePost, entervicePostForm, entervicePut,
} from './enterviceApi';
import type {
  Paged,
  Preset,
  PresetListItem,
  PresetListQuery,
  PresetPayload,
  PresetStatus,
  ProviderDescriptor,
  RankEntry,
  TestCreateInput,
  TestListQuery,
  TestRun,
  TestStarted,
  TestStatus,
  VideoTestInput,
} from '../types/entervice';

/** Drops empty values: vice-service validates `status` with an enum, so `''` must not reach it. */
const clean = (query: object): Record<string, unknown> => {
  const source = query as Record<string, unknown>;
  return Object.keys(source).reduce((acc, key) => {
    const value = source[key];
    if (value !== undefined && value !== null && value !== '') acc[key] = value;
    return acc;
  }, {} as Record<string, unknown>);
};

/** ENTER VICE preset studio calls (SCRUM-219); every method maps 1:1 to an admin-service route. */
export class EnterViceService {
  // ---- presets
  static listPresets(query: PresetListQuery) {
    return enterviceGet<Paged<PresetListItem>>('/presets', clean(query));
  }

  static getPreset(id: string) {
    return enterviceGet<Preset>(`/presets/${id}`);
  }

  static createPreset(payload: PresetPayload) {
    return entervicePost<Preset>('/presets', payload);
  }

  static updatePreset(id: string, payload: Partial<PresetPayload>) {
    return entervicePut<Preset>(`/presets/${id}`, payload);
  }

  /** 409 PRESET_ACTIVATION_BLOCKED (with `reasons`) when the activation lock is not satisfied. */
  static setPresetStatus(id: string, status: 'active' | 'inactive') {
    return entervicePut<Preset>(`/presets/${id}/status`, { status });
  }

  static setPresetRanks(ranks: RankEntry[]) {
    return entervicePut<{ matched: number; modified: number }>('/presets/ranks', { ranks });
  }

  static duplicatePreset(id: string) {
    return entervicePost<Preset>(`/presets/${id}/duplicate`);
  }

  static deletePreset(id: string) {
    return enterviceDelete<{ presetId: string; status: PresetStatus }>(`/presets/${id}`);
  }

  static thumbnailFromTest(id: string, testId: string) {
    return entervicePost<{ presetId: string; thumbnailUrl: string; fromTestId: string }>(`/presets/${id}/thumbnail`, { testId });
  }

  // ---- test lab
  static createTest(input: TestCreateInput) {
    const fields: Record<string, unknown> = clean({
      kind: 'image',
      presetId: input.presetId,
      sourceImageUrl: input.sourceImageUrl,
      prompt: input.prompt,
      negativePrompt: input.negativePrompt,
      provider: input.provider,
      model: input.model,
    });
    if (input.params && Object.keys(input.params).length > 0) fields.params = input.params;
    if (input.file) {
      const form = new FormData();
      Object.keys(fields).forEach((key) => {
        const value = fields[key];
        form.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
      });
      form.append('image', input.file, input.file.name);
      return entervicePostForm<TestStarted>('/tests', form);
    }
    return entervicePost<TestStarted>('/tests', fields);
  }

  /** Animate a finished image test (SCRUM-224); fields left out fall back to the preset's saved video block. */
  static createVideoTest(parentTestId: string, input: VideoTestInput) {
    return entervicePost<TestStarted>(`/tests/${parentTestId}/video`, clean(input));
  }

  static listTests(query: TestListQuery) {
    return enterviceGet<Paged<TestRun>>('/tests', clean(query));
  }

  static getTest(id: string) {
    return enterviceGet<TestRun>(`/tests/${id}`);
  }

  static cancelTest(id: string) {
    return enterviceDelete<{ jobId: string; status: TestStatus; cancelRequested?: boolean }>(`/tests/${id}`);
  }

  // ---- providers
  static getProviders() {
    return enterviceGet<ProviderDescriptor[]>('/providers');
  }
}
